import * as React from 'react';
import { useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Slider from '@mui/material/Slider';
import styled from '@mui/material/styles/styled';
import { TrainingRun } from '../../services/TrainingRunsService';
import { AppContext } from '../../contexts/AppContext';
import { getTotalSectionNutrition } from '../../utils/NutritionUtils';
import { convertDistance, convertElevation } from '../../utils/UnitsUtils';
import NoteBox from '../plans/NoteBox';
import NutritionProductsSection from '../nutrition/NutritionProductsSection';
import NutritionSummaryTabs from '../nutrition/NutritionSummaryTabs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const TitleBox = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(-1),
  marginRight: theme.spacing(-1),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.grey[400]}`,
}));

interface TrainingRunStep3DetailsProps {
  trainingRun: Partial<TrainingRun>;
  onUpdate: (updated: Partial<TrainingRun>) => void;
}

const TrainingRunStep3Details: React.FC<TrainingRunStep3DetailsProps> = observer(({
  trainingRun,
  onUpdate
}) => {
  const intl = useIntl();
  const { userService, nutritionProductsService } = React.useContext(AppContext);

  const hours = Math.floor((trainingRun.time || 0) / 60);
  const minutes = (trainingRun.time || 0) % 60;
  const [hoursStr, setHoursStr] = React.useState<string>(hours.toString());
  const [minutesStr, setMinutesStr] = React.useState<string>(minutes.toString());

  React.useEffect(() => {
    const h = Math.floor((trainingRun.time || 0) / 60);
    const m = (trainingRun.time || 0) % 60;
    setHoursStr(h.toString());
    setMinutesStr(m.toString());
  }, [trainingRun.time]);

  React.useEffect(() => {
    if (!userService.user?.id) {
      return;
    }
    nutritionProductsService.loadNutritionProducts(userService.user.id);
  }, [nutritionProductsService, userService.user?.id]);

  const nutritionValues = getTotalSectionNutrition(
    trainingRun.nutrition || [],
    nutritionProductsService.nutritionProducts
  );

  const timeInHours = (trainingRun.time || 0) / 60;
  const nutritionPerHour = {
    water: timeInHours ? nutritionValues.water / timeInHours : 0,
    calories: timeInHours ? nutritionValues.calories / timeInHours : 0,
    carbs: timeInHours ? nutritionValues.carbs / timeInHours : 0,
    protein: timeInHours ? nutritionValues.protein / timeInHours : 0,
    fat: timeInHours ? nutritionValues.fat / timeInHours : 0,
    sodium: timeInHours ? nutritionValues.sodium / timeInHours : 0,
    caffeine: timeInHours ? nutritionValues.caffeine / timeInHours : 0
  };

  const nutritionSummary: Record<string, typeof nutritionValues> = {
    run: nutritionValues,
    hour: nutritionPerHour
  };

  const distanceUnit = userService.user?.distanceUnits === 'miles' ? 'miles' : 'km';
  const elevationUnit = userService.user?.elevationUnits === 'feet' ? 'feet' : 'm';

  return (
    <Box sx={{ mt: 3, textAlign: 'left' }}>
      <Box display="flex" flexDirection="column" gap={2} mb={2}>
        <TextField
          fullWidth
          label={intl.formatMessage({ id: "form.planName" })}
          value={trainingRun.name || ''}
          onChange={(e) => onUpdate({ ...trainingRun, name: e.target.value })}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={intl.formatMessage({ id: "form.adventureDate" })}
            value={trainingRun.date ? dayjs(trainingRun.date) : null}
            onChange={(newValue) => onUpdate({ ...trainingRun, date: newValue?.toDate() })}
          />
        </LocalizationProvider>

        <TextField
          fullWidth
          type="number"
          label={`${intl.formatMessage({ id: "data.distanceTitle" })} (${distanceUnit})`}
          value={trainingRun.distance != null ? convertDistance(trainingRun.distance, userService.user?.distanceUnits || 'km') : ''}
          onChange={(e) => {
            const parsed = parseFloat(e.target.value);
            const valueInKm = isNaN(parsed) ? 0 : userService.user?.distanceUnits === 'miles'
              ? parsed * 1.60934
              : parsed;
            onUpdate({ ...trainingRun, distance: valueInKm });
          }}
        />

        <Box display="flex" gap={2} alignItems="center">
          <Typography variant="body1" width={100}>
            {intl.formatMessage({ id: "trainingRun.elapsedTime" })}
          </Typography>
          <TextField
            type="number"
            label={intl.formatMessage({ id: "trainingRun.hours" })}
            value={hoursStr}
            onChange={(e) => {
              let value = e.target.value.replace(/[^0-9]/g, "");
              setHoursStr(value);
              const totalMinutes = (value ? parseInt(value) : 0) * 60 + (minutesStr ? parseInt(minutesStr) : 0);
              onUpdate({ ...trainingRun, time: totalMinutes });
            }}
          />
          <TextField
            type="number"
            label={intl.formatMessage({ id: "trainingRun.minutes" })}
            value={minutesStr}
            onChange={(e) => {
              let value = e.target.value.replace(/[^0-9]/g, "");
              if (parseInt(value) > 59) {
                value = "59";
              }
              setMinutesStr(value);
              const totalMinutes = (hoursStr ? parseInt(hoursStr) : 0) * 60 + (value ? parseInt(value) : 0);
              onUpdate({ ...trainingRun, time: totalMinutes });
            }}
          />
        </Box>

        <TextField
          fullWidth
          type="number"
          label={`${intl.formatMessage({ id: "data.elevationGainTitle" })} (${elevationUnit})`}
          value={trainingRun.elevationGain != null ? convertElevation(trainingRun.elevationGain, userService.user?.elevationUnits || 'm') : ''}
          onChange={(e) => {
            const parsed = parseFloat(e.target.value);
            const valueInMeters = isNaN(parsed) ? 0 : userService.user?.elevationUnits === 'feet'
              ? parsed * 0.3048
              : parsed;
            onUpdate({ ...trainingRun, elevationGain: valueInMeters });
          }}
        />

        <TextField
          fullWidth
          type="number"
          label={`${intl.formatMessage({ id: "data.elevationLossTitle" })} (${elevationUnit})`}
          value={trainingRun.elevationLoss != null ? convertElevation(trainingRun.elevationLoss, userService.user?.elevationUnits || 'm') : ''}
          onChange={(e) => {
            const parsed = parseFloat(e.target.value);
            const valueInMeters = isNaN(parsed) ? 0 : userService.user?.elevationUnits === 'feet'
              ? parsed * 0.3048
              : parsed;
            onUpdate({ ...trainingRun, elevationLoss: valueInMeters });
          }}
        />
      </Box>

      {/* Nutrition */}
      <TitleBox />

      <NutritionProductsSection
        nutrition={trainingRun.nutrition || []}
        setNutrition={(nutrition) => onUpdate({ ...trainingRun, nutrition })}
        showAddFavorite={false}
      />

      {/* Nutrition Summary */}
      <NutritionSummaryTabs
        nutritionValues={nutritionSummary}
        tabs={[
          { value: "hour", labelId: "data.totalNutritionPerHour" },
          { value: "run", labelId: "trainingRun.totalForRun" }
        ]}
        defaultTab="hour"
      />

      {/* Sliders */}
      <TitleBox>
        <Typography variant="body2" fontWeight="bold">
          {intl.formatMessage({ id: "trainingRun.feelings" })}
        </Typography>
      </TitleBox>

      <Box mt={2} display="grid" gridTemplateColumns="auto auto 1fr auto" gap={2} alignItems="center">
        <Typography variant="body2" noWrap>
          {intl.formatMessage({ id: "trainingRun.performance" })} 💪
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {intl.formatMessage({ id: "trainingRun.feltWeak" })}
        </Typography>
        <Slider
          value={trainingRun.performance ?? 5}
          onChange={(_, value) => onUpdate({ ...trainingRun, performance: value as number })}
          min={0}
          max={10}
          step={1}
          marks
        />
        <Typography variant="caption" color="text.secondary" align="right" noWrap>
          {intl.formatMessage({ id: "trainingRun.feltStrong" })}
        </Typography>

        <Typography variant="body2" noWrap>
          {intl.formatMessage({ id: "trainingRun.gutIssues" })} 🤢
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {intl.formatMessage({ id: "trainingRun.noIssues" })}
        </Typography>
        <Slider
          value={trainingRun.gutIssues ?? 5}
          onChange={(_, value) => onUpdate({ ...trainingRun, gutIssues: value as number })}
          min={0}
          max={10}
          step={1}
          marks
        />
        <Typography variant="caption" color="text.secondary" align="right" noWrap>
          {intl.formatMessage({ id: "trainingRun.strongIssues" })}
        </Typography>

        <Typography variant="body2" noWrap>
          {intl.formatMessage({ id: "trainingRun.temperature" })} 🌡️
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {intl.formatMessage({ id: "trainingRun.veryCold" })}
        </Typography>
        <Slider
          value={trainingRun.temperature ?? 5}
          onChange={(_, value) => onUpdate({ ...trainingRun, temperature: value as number })}
          min={0}
          max={10}
          step={1}
          marks
        />
        <Typography variant="caption" color="text.secondary" align="right" noWrap>
          {intl.formatMessage({ id: "trainingRun.veryWarm" })}
        </Typography>
      </Box>

      {/* Notes */}
      <TitleBox>
        <Typography variant="body2" fontWeight="bold">
          {intl.formatMessage({ id: "plans.notes" })}
        </Typography>
      </TitleBox>

      <NoteBox
        currentSection={0}
        note={trainingRun.notes || ''}
        onChangeNote={(note) => onUpdate({ ...trainingRun, notes: note })}
      />
    </Box>
  );
});

export default TrainingRunStep3Details;
